<template>
    <button
        @click="$emit('click-event')"
        :type="inputType"
        v-bind="$attrs"
        :disabled="btnDisabled"
        :class="buttonClass"
        class="flex justify-center items-center gap-1"
    >
        <slot name="front-icon"></slot> <!-- 원하는 아이콘 컴포넌트를 넣는다 <template v-slot:front-icon> -->
        <span v-if="btnFrontIconSvg" class="flex-shrink-0">
            <svg
                :width="iconSize"
                :height="iconSize"
                :viewBox="`0 0 ${iconSize} ${iconSize}`"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path :d="btnFrontIconSvg" :class="['fill-current', btnFrontIconColor]" />
            </svg>
        </span>
        <div v-if="btnText" class="whitespace-pre-line">
            {{ btnText }}
        </div>
        <slot name="back-icon"></slot>
        <span v-if="btnBackIconSvg" class="flex-shrink-0">
            <svg
                :width="iconSize"
                :height="iconSize"
                :viewBox="`0 0 ${iconSize} ${iconSize}`"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path :d="btnBackIconSvg" :class="['fill-current', btnBackIconColor]" />
            </svg>
        </span>
    </button>
</template>

<script>
export default {
    emits: ['click-event'],
    props: {
        inputType : { type : String, default : 'button' },
        btnVariant: { type: String, default: 'FILL', required: false }, // type : FILL, BORDER
        // color : PRIMARY, RED... 
        btnType: { type: String, default: 'PRIMARY', required: true },
        // XS, SM, MD, LG
        btnSize: { type: String, default: 'MD', required: false },
        btnText: { type: String, default: null, required: false }, // 아이콘만 있는 버튼의 경우
        btnDisabled: { type: Boolean, default: false, required: false },

        btnFrontIconSvg: { type: String, default: null, required: false },
        btnFrontIconColor: { type: String, default: null, required: false },
        btnBackIconSvg: { type: String, default: null, required: false },
        btnBackIconColor: { type: String, default: null, required: false },
    },
    data() {
        return {
            sizeMap: {
                XL: 'h-[4.25rem] px-3 rounded-2 text-base font-semibold leading-[1.375rem]',
                LG: 'h-[3.5rem] px-3 rounded-2 font-body-md-medium',
                MD: 'h-[2.75rem] px-3 rounded-2 font-body-sm-medium',
                SM: 'h-[2.25rem] px-2 rounded-2 font-medium leading-normal text-xs',
                XS: 'h-[2rem] px-1 rounded-2 font-medium font-inter-caption-1 rounded-01',
                FULL : 'h-[2.75rem] w-full px-3 rounded-2 font-body-sm-medium',
            },
            // 기존 버튼 컴포넌트 type 중 사용하고 있는 것 유지, PRIMARY 는 신규 컴포넌트로 유지
            legacyTypeStyles: {
                WHITE: {
                    bgClass:
                        'bg-background-basic-1 hover:bg-background-basic-2 active:bg-background-basic-3 disabled:bg-background-basic-1',
                    textClass:
                        'text-content-basic-1 disabled:text-color-basic-3',
                    borderClass: '',
                },
                RED_SOLID: {
                    bgClass:
                        'bg-accent-red-3 hover:bg-accent-red-4 active:bg-accent-red-4 disabled:bg-accent-red-2',
                    textClass:
                        'text-content-inverse-1',
                    borderClass: '',
                },
                PRIMARY_LINE: {
                    bgClass:
                        'bg-background-basic-1 hover:bg-background-basic-2 disabled:bg-background-basic-1',
                    textClass:
                        'text-accent-blue-3 disabled:text-content-basic-3',
                    borderClass: 'border border-accent-blue-3 disabled:border-background-basic-1',
                },
                QUATERNARY : {
                    bgClass : 'bg-background-basic-1 hover:bg-background-basic-2 active:bg-background-basic-3 disabled:bg-background-basic-1',
                    textClass : 'text-content-basic-1 disabled:text-content-basic-4',
                    borderClass : 'border border-border-basic-2 hover:border-border-basic-2 active:border-border-basic-2 disabled:border-border-basic-1',
                },

            },
            colorVariantStyles: {
                PRIMARY: {
                    FILL: {
                        bgClass:
                            'bg-primary-3 hover:bg-primary-4 disabled:bg-background-basic-5',
                        textClass:
                            'text-content-inverse-1 hover:text-content-inverse-1 disabled:text-content-basic-3',
                        borderClass: '',
                    },
                    BORDER: {
                        bgClass: 'hover:bg-primary-1',
                        textClass:
                            ' text-primary-3 disabled:text-content-basic-3',
                        borderClass:
                            'border border-primary-3 disabled:border-border-basic-2',
                    },
                },
                SECONDARY : {
                    FILL: {
                        bgClass:
                            'bg-background-basic-4 hover:bg-background-basic-5 disabled:bg-background-basic-5',
                        textClass:
                            'text-content-basic-1 hover:text-content-basic-1 disabled:text-content-basic-3',
                        borderClass: '',
                    },
                    BORDER: {
                        bgClass: 'bg-background-basic-1',
                        textClass:
                            'hover:text-content-basic-1 text-content-basic-1 disabled:text-content-basic-3',
                        borderClass:
                            'border border-border-basic-2 hover:bg-background-basic-2 disabled:border-border-basic-2 disabled:hover:bg-transparent-basic-0',
                    },
                },
                GREEN: {
                    FILL: {
                        bgClass:
                            'disabled:bg-background-basic-5 bg-accent-green-3 hover:bg-accent-green-4',
                        textClass:
                            'disabled:text-content-basic-3 text-content-inverse-1 hover:text-content-inverse-1',
                        borderClass: '',
                    },
                    BORDER: {
                        bgClass: 'bg-background-basic-1',
                        textClass:
                            'disabled:text-content-basic-3 text-accent-green-3 hover:text-accent-green-4',
                        borderClass:
                            'disabled:border-border-basic-2 border-accent-green-3 hover:border-accent-green-4',
                    },
                },
                LIGHT_BLUE: {
                    FILL: {
                        bgClass:
                            'disabled:bg-background-basic-5 bg-accent-lightBlue-3 hover:bg-accent-lightBlue-4',
                        textClass:
                            'disabled:text-content-basic-3 text-content-inverse-1 hover:text-content-inverse-1',
                        borderClass: '',
                    },
                    BORDER: {
                        bgClass: 'bg-background-basic-1',
                        textClass:
                            'disabled:text-content-basic-3 text-accent-lightBlue-3 hover:text-accent-lightBlue-4',
                        borderClass:
                            'disabled:border-border-basic-2 border-accent-lightBlue-3 hover:border-accent-lightBlue-4',
                    },
                },
                RED: {
                    FILL: {
                        bgClass:
                            'disabled:bg-background-basic-5 bg-accent-red-3 hover:bg-accent-red-4',
                        textClass:
                            'disabled:text-content-basic-3 text-content-inverse-1 hover:text-content-inverse-1',
                        borderClass: '',
                    },
                    BORDER: {
                        bgClass: 'bg-background-basic-1',
                        textClass:
                            'disabled:text-content-basic-3 text-accent-red-3 hover:text-accent-red-4',
                        borderClass:
                            'border disabled:border-border-basic-2 border-accent-red-3 hover:border-accent-red-4',
                    },
                },
            },
        };
    },
    computed: {
        iconSize() {
            const size = this.btnSize.toUpperCase();
            return size === 'XL' ? 20 : 16;
        },

        buttonClass() {
            const self = this;
            const type = self.btnType.toUpperCase();
            const size = self.btnSize.toUpperCase();
            const variant = (self.btnVariant || 'FILL').toUpperCase();

            const sizeClass = self.sizeMap[size] || '';
            const { bgClass, textClass, borderClass } = self.getStyleByType(type, variant);

            return [sizeClass, bgClass, textClass, borderClass]
                .filter(Boolean)
                .join(' ');
        },
    },

    methods: {
        getStyleByType(type, variant) {
            if (this.legacyTypeStyles[type]) {
                return this.legacyTypeStyles[type];
            }

            const colorConfig = this.colorVariantStyles[type];
            if (colorConfig && colorConfig[variant]) {
                return colorConfig[variant];
            }

            return { bgClass: '', textClass: '', borderClass: '' };
        },
    },
};
</script>
